import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { support } from "../lib/supportApi";
import type { Thread } from "../lib/supportApi";
import { useApi } from "../lib/useApi";
import { useAction } from "./ProjectTools";
import { Button, CardSkeleton } from "../components/ui";
import ReceiptForm, { RECEIPT_ACCEPT, receiptError } from "../components/ReceiptForm";
import { relativeTime } from "../lib/format";

export default function Support({ operator = false }: { operator?: boolean }) {
  const [params, setParams] = useSearchParams();
  const selected = params.get("ticket") ?? "";
  const setSelected = (id: string) => setParams(current => { if (id) current.set("ticket", id); else current.delete("ticket"); return current });
  const
    [compose, setCompose] = useState(!operator && params.get("new") === "1"),
    [query, setQuery] = useState(""),
    [statusFilter, setStatusFilter] = useState(""),
    [offset, setOffset] = useState(0);
  const { data, error, reload } = useApi(
    () => support.tickets(operator, query, statusFilter, offset),
    [operator, query, statusFilter, offset], 15_000,
  );
  const visible = data;
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">
            {operator ? "OPERATOR SUPPORT INBOX" : "TALARICLOUD SUPPORT"}
          </p>
          <h1>
            {operator ? "Help customers move forward" : "How can we help?"}
          </h1>
          <p className="subtitle">
            Conversations, deployment help, and bank-transfer evidence.
          </p>
        </div>
        {!operator && (
          <Button onClick={() => setCompose(!compose)}>New conversation</Button>
        )}
      </header>
      {error && <p className="alert error">{error}</p>}
      {compose && (
        <NewTicket
          billing={params.get("category") === "billing"}
          initialAmount={params.get("amount") ?? ""}
          done={(id) => {
            setSelected(id);
            setCompose(false);
            reload();
          }}
        />
      )}
      <div className="support-layout">
        <aside className="card support-list">
          <label className="sr-only" htmlFor="support-search">
            Search conversations
          </label>
          <input
            id="support-search"
            type="search"
            placeholder="Search conversations…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOffset(0); }}
          />
          <label>Status<select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setOffset(0); }}>
            <option value="">All conversations</option>{["open", "in_progress", "waiting_on_customer", "resolved"].map(v => <option key={v} value={v}>{v.replaceAll("_", " ")}</option>)}
          </select></label>
          {!data && !error && <CardSkeleton />}
          {visible?.length === 0 && (
            <p className="muted">No matching conversations.</p>
          )}
          {visible?.map((t) => (
            <button
              key={t.id}
              className={`support-ticket ${selected === t.id ? "selected" : ""}`}
              onClick={() => setSelected(t.id)}
            >
              <span className="platform-role">
                {t.category.replaceAll("_", " ")} ·{" "}
                {t.status.replaceAll("_", " ")}
              </span>
              <strong>{t.subject}</strong>
              {!!t.unread_count && <span className="platform-role">{t.unread_count} unread</span>}
              {operator && <span className="tiny muted">{t.owner_email}</span>}
              <span className="tiny muted">
                Updated {relativeTime(t.updated_at)}
              </span>
            </button>
          ))}
          <div className="row"><Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))}>Previous</Button>
            <Button disabled={!data || data.length < 100} onClick={() => setOffset(offset + 100)}>Next</Button></div>
        </aside>
        {selected ? (
          <Conversation
            key={selected}
            id={selected}
            operator={operator}
            onUpdate={reload}
          />
        ) : (
          <section className="card empty">
            <h2>Your support workspace</h2>
            <p>
              Select a conversation or start a new one. Receipts are visible
              only to your account and authorized operators.
            </p>
          </section>
        )}
      </div>
    </>
  );
}

function NewTicket({ done, billing, initialAmount }: { done: (id: string) => void; billing: boolean; initialAmount: string }) {
  const [category, setCategory] = useState(billing ? "billing" : "technical"),
    [subject, setSubject] = useState(billing ? "Wallet top-up request" : ""),
    [message, setMessage] = useState(billing ? `I would like to add ${initialAmount || "credit"} ${initialAmount ? "ETB " : ""}to my wallet. Please send payment instructions.` : ""),
    [amount, setAmount] = useState(""),
    [reference, setReference] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [createdId, setCreatedId] = useState("");
  const { busy, run, feedback } = useAction(() => {});
  if (billing) return <section className="card stack"><h2>Submit deposit evidence</h2><ReceiptForm initialAmount={initialAmount} /></section>;
  return (
    <form
      className="card stack"
      style={{ marginBottom: 24 }}
      onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          if (category === "bank_transfer" && !file && !reference.trim()) throw new Error("Attach a receipt or enter a transaction reference.");
          if (receiptError(file)) throw new Error(receiptError(file));
          const ticket = createdId ? { id: createdId } : await support.create({
            category,
            subject,
            message,
            ...(category === "bank_transfer"
              ? { transfer_amount: amount, transfer_reference: reference }
              : {}),
          });
          setCreatedId(ticket.id);
          if (file) await support.upload(ticket.id, file);
          done(ticket.id);
        }, "Conversation started");
      }}
    >
      {feedback}
      <fieldset disabled={busy || !!createdId} className="stack">
      <div className="advanced-form">
        <label>
          Topic
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="technical">Technical support</option>
            <option value="billing">Billing</option>
            <option value="bank_transfer">Bank-transfer evidence</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Subject
          <input
            required
            minLength={3}
            maxLength={160}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
      </div>
      {category === "bank_transfer" && (
        <>
          <div className="advanced-form">
            <label>
              Amount sent (ETB)
              <input
                type="number"
                required
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <label>
              Bank transaction reference (optional with receipt)
              <input
                maxLength={128}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </label>
          </div>
          <p className="platform-notice">
            Attach your bank receipt below. An
            operator will verify the transfer. Evidence submission does not
            automatically credit your wallet.
          </p>
        </>
      )}
      <label>
        How can we help?
        <textarea
          required
          rows={4}
          maxLength={10000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe what happened and what you expected. Include a service name or deployment ID if relevant."
        />
      </label>
      </fieldset>
      <label>Attach image or PDF (optional)<input type="file" accept={RECEIPT_ACCEPT} disabled={busy} onChange={e => setFile(e.target.files?.[0] ?? null)} /></label>
      {receiptError(file) && <p className="alert error" role="alert">{receiptError(file)}</p>}
      {createdId && <p className="tiny muted">Conversation saved. Retry to send the attachment, or <Link to={`/support?ticket=${createdId}`}>open the conversation</Link>.</p>}
      <p className="tiny muted">PNG, JPG, or PDF · Up to 10 MB</p>
      <Button type="submit" loading={busy} disabled={!!receiptError(file)}>
        {createdId ? "Retry attachment" : "Send to Support"}
      </Button>
    </form>
  );
}

function Conversation({
  id,
  operator,
  onUpdate,
}: {
  id: string;
  operator: boolean;
  onUpdate: () => void;
}) {
  const { data, error, reload } = useApi(() => support.thread(id), [id], 5_000);
  const [older, setOlder] = useState<Thread["messages"]>([]),
    [hasOlder, setHasOlder] = useState<boolean | null>(null);
  const [message, setMessage] = useState(""),
    [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState(""),
    [review, setReview] = useState(""),
    [note, setNote] = useState("");
  const { busy, run, feedback } = useAction(() => {
    reload();
    onUpdate();
  });
  const latestMessage = data?.messages.at(-1)?.id;
  useEffect(() => {
    if (!latestMessage) return;
    void support.markRead(id, latestMessage).then(onUpdate).catch(() => {});
  }, [id, latestMessage, onUpdate]);
  if (error) return <p className="alert error">{error}</p>;
  if (!data) return <CardSkeleton />;
  const messages = [...older, ...data.messages].filter(
    (m, i, all) => all.findIndex((a) => a.id === m.id) === i,
  );
  return (
    <section className="card stack support-conversation">
      <header>
        <p className="platform-role">{data.status.replaceAll("_", " ")}</p>
        <h2>{data.subject}</h2>
        <p className="tiny muted">Conversation {data.id.slice(0, 8)}</p>
      </header>
      {feedback}
      {data.category === "bank_transfer" && (
        <div className="platform-notice">
          <strong>Bank transfer · {data.transfer_amount} ETB</strong>
          <p>Reference: {data.transfer_reference || "See attached receipt"}</p>
          <span>Evidence review: {data.transfer_review}</span>
          <p className="tiny muted">
            Wallet credit is a separate verified settlement action.
          </p>
        </div>
      )}
      {(hasOlder ?? data.has_older) && (
        <Button
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const result = await support.thread(id, messages[0]?.id);
              setOlder((current) => [...result.messages, ...current]);
              setHasOlder(result.has_older);
            }, "Earlier messages loaded")
          }
        >
          Load earlier messages
        </Button>
      )}
      <div className="support-messages" aria-label="Conversation messages">
        {messages.map((m) => (
          <article
            className={`support-bubble ${m.is_operator ? "operator" : "customer"}`}
            key={m.id}
          >
            <p className="tiny muted">
              {m.is_operator ? "TalariCloud support" : "Customer"} ·{" "}
              {new Date(m.created_at).toLocaleString()}
            </p>
            <p className="support-message-text">{m.body}</p>
          </article>
        ))}
      </div>
      <section>
        <h3>Evidence & attachments</h3>
        {data.attachments.length === 0 && (
          <p className="tiny muted">No attachments yet.</p>
        )}
        {data.attachments.map((a) => <Attachment key={a.id} ticket={id} attachment={a} />)}
      </section>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            if (receiptError(file)) throw new Error(receiptError(file));
            if (message.trim()) { await support.send(id, message); setMessage(""); }
            if (file) { await support.upload(id, file); setFile(null); }
          }, "Message sent");
        }}
      >
        <label>
          Message
          <textarea
            rows={3}
            maxLength={10000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        <label>Attach image or PDF<input key={file ? 'selected' : 'empty'} type="file" accept={RECEIPT_ACCEPT} disabled={busy} onChange={e => setFile(e.target.files?.[0] ?? null)} /></label>
        <p className="tiny muted">PNG, JPG, or PDF · Up to 10 MB · 10 files per conversation</p>
        {file && <p className="tiny">Ready to send: {file.name}</p>}
        {receiptError(file) && <p className="alert error" role="alert">{receiptError(file)}</p>}
        <Button type="submit" loading={busy} disabled={(!message.trim() && !file) || !!receiptError(file)}>
          Send
        </Button>
      </form>
      {operator && (
        <details className="workspace-review">
          <summary>Operator review & customer support</summary>
          <Link to={`/support-account/${data.owner_id}`}>
            Open customer support session →
          </Link>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                () =>
                  support.update(id, {
                    status: status || data.status,
                    note,
                    ...(data.category === "bank_transfer"
                      ? { transfer_review: review || data.transfer_review }
                      : {}),
                  }),
                "Review recorded",
              );
            }}
          >
            <label>
              Status
              <select
                value={status || data.status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {["open", "in_progress", "waiting_on_customer", "resolved"].map(
                  (v) => (
                    <option key={v} value={v}>
                      {v.replaceAll("_", " ")}
                    </option>
                  ),
                )}
              </select>
            </label>
            {data.category === "bank_transfer" && (
              <label>
                Transfer evidence review
                <select
                  value={review || data.transfer_review || "pending"}
                  onChange={(e) => setReview(e.target.value)}
                >
                  {["pending", "verified", "rejected"].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Customer-visible review note
              <textarea
                required
                minLength={1}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <Button type="submit" loading={busy}>
              Record review
            </Button>
          </form>
        </details>
      )}
    </section>
  );
}


function Attachment({ ticket, attachment: a }: { ticket: string; attachment: Thread["attachments"][number] }) {
  const [url, setUrl] = useState<string | null>(null);
  const { busy, run, feedback } = useAction(() => {});
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const picture = ["image/png", "image/jpeg"].includes(a.content_type);
  return <div className="stack">
    <div className="workspace-dependency"><span>{a.filename} · {(a.size_bytes / 1024).toFixed(0)} KB</span><div className="row">
      {picture && <Button disabled={busy} onClick={() => {
        if (url) { setUrl(null); return; }
        void run(async () => {
          const blob = await support.picture(ticket, a.id);
          setUrl(URL.createObjectURL(new Blob([blob], { type: a.content_type })));
        }, "Picture opened");
      }}>{url ? "Hide picture" : "View picture"}</Button>}
      <Button disabled={busy} onClick={() => void run(() => support.download(ticket, a.id, a.filename), "Attachment downloaded")}>Download</Button>
    </div></div>
    {feedback}
    {url && <img className="support-image-preview" src={url} alt={`Support attachment: ${a.filename}`} />}
  </div>;
}

